/* global updateClientGroupSubcategoryUrl storeClientGroupSecondSubcategoryUrl */

import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Modal } from 'flowbite';

import ClientGroupNumberSecondSubcategoryRow from './ClientGroupNumberSecondSubcategoryRow';
import ClientGroupSourceTag from './ClientGroupSourceTag';
import ConfirmAddSubcategoryModal from './ConfirmAddSubcategoryModal';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { getIntlLocale } from 'Helpers/localeService';
import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function ClientGroupNumberSubcategoryRow(props) {
    const toast = useToast();
    const [toggle, setToggle] = useState(false);
    const [editing, setEditing] = useState(props.subcategory.isNew ?? false);
    // inlineEditing: click-to-edit mode for system-linked (non-event) rows
    const [inlineEditing, setInlineEditing] = useState(false);
    const [deleted, setDeleted] = useState(props.subcategory.isDeleted ?? false);
    const [name, setName] = useState(props.subcategory.name);
    const [value, setValue] = useState(props.subcategory.value ?? 0);
    const [inlineValue, setInlineValue] = useState(null);
    const [secondSubcategories, setSecondSubcategories] = useState([]);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [categoryDeleteModalElement, setCategoryDeleteModalElement] = useState(null);
    const categoryDeleteModalRef = useRef();

    const confirmAddSubcategoryModal = useModal();

    const cleanCategoryDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const successHandlerDelete = () => {
        hideModalElement(categoryDeleteModalElement);
        setDeleted(true);
        props.subcategory.value = 0;
        props.subcategory.isDeleted = true;
        props.calculateCategoryTotal();
    };

    const triggerDelete = (category_id) => {
        setActionInProgress(true);
        deleteSubcategory(category_id);
    };

    const closeModal = () => {
        hideModalElement(categoryDeleteModalElement);
    };

    useEffect(() => {
        setSecondSubcategories(props.subcategory.client_group_second_sub_categories);
        calculateSubcategoryTotal();
    }, []);

    const toggleEditing = () => {
        props.setError('');
        setEditing(!editing);
    };

    const handleChangeName = (event) => {
        setName(event.target.value);
    };

    const handleChangeValue = (values) => {
        setValue(values.floatValue);
    };

    const saveEditing = (subcategory_id) => {
        props.setError('');

        props.subcategory.isNew = false;

        const doSave = () => {
            axios
                .put(updateClientGroupSubcategoryUrl.replace('CLIENT_GROUP_SUBCATEGORY_ID', subcategory_id), {
                    name: name,
                    value: value,
                })
                .then((res) => {
                    props.subcategory.name = res.data.clientGroupSubcategory.name;
                    props.subcategory.value = res.data.clientGroupSubcategory.value;
                    props.calculateCategoryTotal();
                    setEditing(false);
                })
                .catch(() => {
                    props.setError('There was an error editing the Sub Category');
                });
        };

        // If quantity linking is enabled, check for downstream dependents before saving
        if (window.quantityLinkEnabled && window.quantityLinkDriverImpactPreviewUrl && props.subcategory.event_id) {
            const baseUrl = window.quantityLinkDriverImpactPreviewUrl;
            const params = new URLSearchParams({
                source_type: 'App\\ClientGroupSubcategory',
                source_id: String(subcategory_id),
            });
            const previewUrl = baseUrl + '?' + params.toString();

            window.dispatchEvent(
                new CustomEvent('quantityimpact:check', {
                    detail: {
                        previewUrl: previewUrl,
                        newValue: value ?? 0,
                        formEl: null,
                        onConfirm: doSave,
                        sourceLabel: props.subcategory.name,
                    },
                }),
            );
        } else {
            doSave();
        }
    };

    const deleteSubcategory = (subcategory_id) => {
        props.setError('');

        axios
            .delete(updateClientGroupSubcategoryUrl.replace('CLIENT_GROUP_SUBCATEGORY_ID', subcategory_id))
            .then((res) => {
                setActionCompleted(true);
            })
            .catch((error) => {
                setActionFailed(true);
                props.setError('There was an error deleting the Sub Category');
            });
    };

    const storeNewSecondSubcategory = () => {
        props.setError('');
        confirmAddSubcategoryModal.hide();

        axios
            .post(storeClientGroupSecondSubcategoryUrl.replace('CLIENT_GROUP_SUBCATEGORY_ID', props.subcategory.id), {
                name: 'New Second Subcategory',
            })
            .then((res) => {
                const updatedSecondSubCategories = [
                    ...secondSubcategories,
                    {
                        id: res.data.clientGroupSecondSubcategory.id,
                        name: res.data.clientGroupSecondSubcategory.name,
                        value: res.data.clientGroupSecondSubcategory.value,
                        event_id: res.data.clientGroupSecondSubcategory.event_id,
                        client_group_subcategory_id: res.data.clientGroupSecondSubcategory.client_group_subcategory_id,
                        isNew: true,
                    },
                ];
                setSecondSubcategories(updatedSecondSubCategories);
                props.subcategory.client_group_second_sub_categories = updatedSecondSubCategories;
                setToggle(true);
            })
            .catch((error) => {
                props.setError('There was an error creating the New Sub Category');
            });
    };

    const calculateSubcategoryTotal = () => {
        if (hasSubcategories()) {
            const thisSubcategories = secondSubcategories.length > 0 ? secondSubcategories : props.subcategory.client_group_second_sub_categories;
            const thisSubcategoryTotal = thisSubcategories.reduce((categoryTotal, subcategory) => {
                return categoryTotal + (!subcategory.event_id ? Number(props.staticData[subcategory.slug]) : Number(subcategory.value));
            }, 0);
            setValue(thisSubcategoryTotal);
            props.subcategory.value = thisSubcategoryTotal;
            props.calculateCategoryTotal();
            setSecondSubcategories(thisSubcategories.filter((subcategory) => !subcategory.isDeleted));
        }
    };

    const hasSubcategories = () => {
        return secondSubcategories.filter((secondSubcategory) => !secondSubcategory.isDeleted).length > 0;
    };

    useEffect(() => {
        if (!deleted) {
            setCategoryDeleteModalElement(new Modal(categoryDeleteModalRef.current, modalOptions(false)));
        }
    }, [deleted]);

    const showDeleteCategory = () => {
        categoryDeleteModalElement && categoryDeleteModalElement.show();
    };

    // Sync collapse/expand with parent broadcast
    useEffect(() => {
        if (props.expandKey > 0) {
            setToggle(props.forceExpanded);
        }
    }, [props.expandKey, props.forceExpanded]);

    const getBySportSubcategories = (slug) => {
        if (slug === 'athletes') return props.staticData.athletes_by_sport;
        if (slug === 'team_officials') return props.staticData.team_officials_by_sport;
        if (slug === 'technical_delegates') return props.staticData.technical_delegates_by_sport;
        if (slug === 'international_technical_officials') return props.staticData.international_technical_officials_by_sport;
        if (slug === 'national_technical_officials') return props.staticData.national_technical_officials_by_sport;
        return [];
    };

    // Map subcategory slug to the short sport slug stored on per-sport overrides
    // (matches ClientGroupNumbersService::SPORT_OVERRIDE_FIELDS and the by_sport "key" prefix).
    const sportSlugForSubcategory = (slug) => {
        if (slug === 'athletes') return 'athletes';
        if (slug === 'team_officials') return 'team_officials';
        if (slug === 'technical_delegates') return 'td';
        if (slug === 'international_technical_officials') return 'ito';
        if (slug === 'national_technical_officials') return 'nto';
        return null;
    };

    const parseSportIdFromKey = (key) => {
        const match = /_sport_id___(\d+)$/.exec(String(key ?? ''));
        return match ? Number(match[1]) : null;
    };

    // ── Override helpers ──────────────────────────────────────────────────
    const overrideKey = `subcategory_${props.subcategory.id}`;
    const currentOverride = props.overrides ? props.overrides[overrideKey] : null;
    const subcategorySourceTag = props.sourceTags ? props.sourceTags[`subcategory_${props.subcategory.id}`] : null;

    // A subcategory is overridable when:
    //  - it is a system row (event_id === null)
    //  - it has no second-subcategories (leaf node)
    //  - not a sport aggregator slug
    const isOverridableLeaf =
        !props.subcategory.event_id &&
        !hasSubcategories() &&
        !Object.hasOwn(props.staticData, `${props.subcategory.slug}_by_sport`) &&
        !['total_athletes', 'total_team_officials', 'total_t_ds', 'total_i_t_os', 'total_n_t_os'].includes(props.subcategory.slug);

    const displayValue = () => {
        if (isOverridableLeaf && currentOverride) {
            return currentOverride.value;
        }
        if (
            !props.subcategory.event_id &&
            !['total_athletes', 'total_team_officials', 'total_t_ds', 'total_i_t_os', 'total_n_t_os'].includes(props.subcategory.slug)
        ) {
            return props.staticData[props.subcategory.slug];
        }
        return props.subcategory.value;
    };

    const handleInlineClick = () => {
        if (!isOverridableLeaf || !props.canUpdate) return;
        setInlineValue(displayValue() ?? 0);
        setInlineEditing(true);
    };

    const handleInlineCancel = () => {
        setInlineEditing(false);
        setInlineValue(null);
    };

    const saveInlineOverride = () => {
        const newValue = inlineValue ?? 0;
        axios
            .put(updateClientGroupSubcategoryUrl.replace('CLIENT_GROUP_SUBCATEGORY_ID', props.subcategory.id), {
                value: newValue,
            })
            .then((res) => {
                setInlineEditing(false);
                setInlineValue(null);
                if (res.data.previous_overrider) {
                    const prev = res.data.previous_overrider;
                    toast.warning(
                        $t('Previous override by {{name}} on {{date}} was replaced', {
                            name: prev.name || $t('Unknown'),
                            date: prev.at ? new Date(prev.at).toLocaleDateString(getIntlLocale()) : '',
                        }),
                    );
                }
                if (props.onOverrideChange) {
                    props.onOverrideChange(overrideKey, res.data.override);
                }
                // Update parent category total
                props.calculateCategoryTotal();
            })
            .catch(() => {
                props.setError($t('There was an error saving the override'));
            });
    };

    const resetOverride = (e) => {
        e.preventDefault();
        axios
            .put(updateClientGroupSubcategoryUrl.replace('CLIENT_GROUP_SUBCATEGORY_ID', props.subcategory.id), {
                reset_override: true,
            })
            .then((res) => {
                if (props.onOverrideChange) {
                    props.onOverrideChange(overrideKey, null);
                }
                props.calculateCategoryTotal();
            })
            .catch(() => {
                props.setError($t('There was an error resetting the override'));
            });
    };

    const isOverridden = isOverridableLeaf && !!currentOverride;

    return !deleted ? (
        <>
            <tr
                key={props.subcategory.id}
                className={
                    isOverridden
                        ? '!tw-bg-amber-50'
                        : hasSubcategories() || Object.hasOwn(props.staticData, `${props.subcategory.slug}_by_sport`)
                          ? '!tw-bg-charcoal-10'
                          : '!tw-bg-white'
                }
            >
                {/* Name Section */}

                {!editing && !(hasSubcategories() || Object.hasOwn(props.staticData, `${props.subcategory.slug}_by_sport`)) && (
                    <td className="!tw-pl-6 tw-font-medium tw-text-charcoal-75">
                        <div className="tw-flex tw-items-center tw-gap-x-2 tw-flex-wrap">
                            <span>{props.subcategory.name}</span>
                            {subcategorySourceTag && <ClientGroupSourceTag source={subcategorySourceTag} />}
                            {isOverridden && (
                                <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-700">
                                    <EditIcon className="tw-w-2.5 tw-h-2.5" />
                                    {$t('Overridden')}
                                </span>
                            )}
                        </div>
                    </td>
                )}
                {!editing && (hasSubcategories() || Object.hasOwn(props.staticData, `${props.subcategory.slug}_by_sport`)) && (
                    <td
                        className="!tw-pl-6 tw-font-medium tw-text-charcoal-75 tw-cursor-pointer"
                        onClick={() => {
                            setToggle(!toggle);
                        }}
                    >
                        <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-flex-wrap">
                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                <span>{props.subcategory.name}</span>
                                <span>{toggle ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
                            </div>
                            {subcategorySourceTag && <ClientGroupSourceTag source={subcategorySourceTag} />}
                        </div>
                    </td>
                )}
                {editing && (
                    <td className="!tw-pl-6">
                        <input
                            type="text"
                            step="1"
                            name={`client_group_subcategory_${props.subcategory.id}`}
                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1"
                            id={`client_group_subcategory_${props.subcategory.id}`}
                            value={name}
                            onChange={(event) => handleChangeName(event)}
                        />
                    </td>
                )}

                {/* Value Section */}

                <td className="inbud-numeric-cell tw-font-medium">
                    {/* Inline override input (click-to-edit on system rows) */}
                    {inlineEditing && (
                        <NumberFormat
                            id={`inline_override_subcategory_${props.subcategory.id}`}
                            name={`inline_override_subcategory_${props.subcategory.id}`}
                            value={inlineValue}
                            defaultValue={0}
                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1 inbud-numeric-text"
                            decimalScale={0}
                            thousandSeparator={true}
                            inputMode="numeric"
                            allowNegative={false}
                            autoFocus
                            onValueChange={(vals) => setInlineValue(vals.floatValue ?? 0)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') saveInlineOverride();
                                if (e.key === 'Escape') handleInlineCancel();
                            }}
                        />
                    )}
                    {/* Static display value */}
                    {!inlineEditing && (
                        <div>
                            <Tooltip tooltipType="text" tooltipContent={$t('Click to override')} allowTooltip={isOverridableLeaf && props.canUpdate}>
                                <span
                                    onClick={handleInlineClick}
                                    className={isOverridableLeaf && props.canUpdate ? 'tw-cursor-pointer hover:tw-underline' : ''}
                                >
                                    {!props.subcategory.event_id &&
                                        !['total_athletes', 'total_team_officials', 'total_t_ds', 'total_i_t_os', 'total_n_t_os'].includes(
                                            props.subcategory.slug,
                                        ) &&
                                        formatFigure(isOverridden ? currentOverride.value : props.staticData[props.subcategory.slug])}
                                    {!!props.subcategory.event_id && (!editing || hasSubcategories()) && formatFigure(props.subcategory.value)}
                                    {!!props.subcategory.event_id && !hasSubcategories() && editing && (
                                        <NumberFormat
                                            id={`client_group_subcategory_${props.subcategory.id}`}
                                            name={`client_group_subcategory_${props.subcategory.id}`}
                                            value={value}
                                            defaultValue={0}
                                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1 inbud-numeric-text"
                                            decimalScale={10}
                                            thousandSeparator={true}
                                            inputMode="numeric"
                                            allowNegative={false}
                                            onValueChange={(values) => handleChangeValue(values)}
                                        />
                                    )}
                                </span>
                            </Tooltip>
                            {isOverridden && currentOverride && (
                                <div className="tw-mt-0.5">
                                    <a href="#" onClick={resetOverride} className="tw-text-xs tw-text-charcoal-50 hover:tw-text-charcoal-75 tw-underline">
                                        {$t('Reset to {{value}}', { value: formatToFixedMoneyWithLocale(currentOverride.source_value) })}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Inline save/cancel buttons */}
                    {inlineEditing && (
                        <div className="tw-flex tw-items-center tw-gap-x-2 tw-mt-1">
                            <button
                                type="button"
                                onClick={saveInlineOverride}
                                className="tw-text-xs tw-text-blue tw-font-medium tw-underline tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
                            >
                                {$t('Save')}
                            </button>
                            <button
                                type="button"
                                onClick={handleInlineCancel}
                                className="tw-text-xs tw-text-charcoal-50 tw-font-medium tw-underline tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
                            >
                                {$t('Cancel')}
                            </button>
                        </div>
                    )}
                </td>

                <td>
                    <div className="tw-flex tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            {!!props.subcategory.event_id && editing && !!props.canUpdate && (
                                <TextButton variant="primary" onClick={() => saveEditing(props.subcategory.id)}>
                                    <CircleCheckIcon className="tw-w-3 tw-h-3" /> {$t('Save')}
                                </TextButton>
                            )}
                            {!!props.subcategory.event_id && !editing && !!props.canUpdate && (
                                <TextButton onClick={() => toggleEditing()}>
                                    <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                                </TextButton>
                            )}
                            {props.allowsCreatingSecondSubcategories && !!props.canCreate && (
                                <TextButton
                                    variant="primary"
                                    onClick={() => (props.subcategory.daily_demands_exists ? confirmAddSubcategoryModal.show() : storeNewSecondSubcategory())}
                                >
                                    <CirclePlusIcon className="tw-w-3 tw-h-3" /> {$t('Add Subcategory')}
                                </TextButton>
                            )}
                            {!!props.subcategory.event_id && !!props.canDelete && (
                                <TextButton variant="danger" onClick={() => showDeleteCategory(props.subcategory.id)}>
                                    <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete')}
                                </TextButton>
                            )}
                        </div>
                    </div>
                    <ConfirmationModal
                        closeHandler={closeModal}
                        successHandler={successHandlerDelete}
                        triggerAction={() => triggerDelete(props.subcategory.id)}
                        cleanStates={cleanCategoryDeleteModalStates}
                        allowAction={true}
                        modalId={`delete-second-subcategory-${props.subcategory.id}`}
                        modalRef={categoryDeleteModalRef}
                        modalElement={categoryDeleteModalElement}
                        request={actionInProgress}
                        actionCompleted={actionCompleted}
                        actionFailed={actionFailed}
                        confirmationMsg={$t('Please confirm you want to delete {{name}} as this action cannot be reversed', { name: props.subcategory.name })}
                        actionCompletedMsg={$t('{{name}} deleted', { name: props.subcategory.name })}
                        actionFailedMsg={$t('{{name}} could not be deleted', { name: props.subcategory.name })}
                        title={$t('Delete Sub Category')}
                        cancelButtonText="Cancel"
                        primaryButtonText="Delete"
                    />
                    <ConfirmAddSubcategoryModal modal={confirmAddSubcategoryModal} onConfirm={storeNewSecondSubcategory} />
                </td>
            </tr>
            {toggle &&
                hasSubcategories() &&
                secondSubcategories.map((secondSubcategory) => {
                    return (
                        <ClientGroupNumberSecondSubcategoryRow
                            key={secondSubcategory.id}
                            secondSubcategory={secondSubcategory}
                            staticData={props.staticData}
                            overrides={props.overrides}
                            onOverrideChange={props.onOverrideChange}
                            calculateSubcategoryTotal={() => calculateSubcategoryTotal()}
                            setError={props.setError}
                            canCreate={props.canCreate}
                            canUpdate={props.canUpdate}
                            canDelete={props.canDelete}
                            parentSubcategoryId={props.subcategory.id}
                            parentSubcategoryValue={value}
                        />
                    );
                })}
            {toggle &&
                Object.hasOwn(props.staticData, `${props.subcategory.slug}_by_sport`) &&
                getBySportSubcategories(props.subcategory.slug).map((secondSubcategory) => {
                    const sportSlug = sportSlugForSubcategory(props.subcategory.slug);
                    const sportId = parseSportIdFromKey(secondSubcategory.key);
                    return (
                        <ClientGroupNumberSecondSubcategoryRow
                            key={secondSubcategory.key}
                            secondSubcategory={secondSubcategory}
                            sportSlug={sportSlug}
                            sportId={sportId}
                            staticData={props.staticData}
                            overrides={props.overrides}
                            onOverrideChange={props.onOverrideChange}
                            calculateSubcategoryTotal={() => calculateSubcategoryTotal()}
                            setError={props.setError}
                            canCreate={props.canCreate}
                            canUpdate={props.canUpdate}
                            canDelete={props.canDelete}
                        />
                    );
                })}
        </>
    ) : (
        <></>
    );
}
